import React from 'react'
import { Box, Typography, styled } from '@mui/material'

const Block = styled(Box)({ color: 'var(--ds-night-ink)', minWidth: 0 })

const Eyebrow = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  marginBottom: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
})

const EyebrowGrad = styled('span')({
  background: 'var(--ds-gradient-spectrum)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
})

const ProBadge = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.1em',
  color: 'color-mix(in srgb, var(--ds-night-canvas) 90%, transparent)',
  background: 'var(--ds-gradient-spectrum)',
  padding: '2px 9px',
  borderRadius: 'var(--ds-radius-pill)',
  WebkitTextFillColor: 'initial',
})

const NameRow = styled(Box)({ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' })

const Handle = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 13,
  color: 'color-mix(in srgb, var(--ds-night-ink) 55%, transparent)',
})

const Motto = styled(Typography)({
  marginTop: 13,
  fontSize: 17,
  color: 'color-mix(in srgb, var(--ds-night-ink) 92%, transparent)',
  maxWidth: 480,
  lineHeight: 1.5,
  fontStyle: 'italic',
  letterSpacing: '-0.012em',
})

const MetaRow = styled(Box)({
  marginTop: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
  fontSize: 12.5,
  color: 'color-mix(in srgb, var(--ds-night-ink) 72%, transparent)',
})

export interface ProfileIdBlockProps {
  firstName: string
  lastName: string
  userName?: string
  profession?: string
  location?: string
  motto?: string
  joinYear?: string
  galleryCount?: number
  isPro?: boolean
}

export function ProfileIdBlock({
  firstName,
  lastName,
  userName,
  profession,
  location,
  motto,
  joinYear,
  galleryCount,
  isPro,
}: ProfileIdBlockProps) {
  const eyebrowParts = [profession, location].filter(Boolean).join(' · ')

  return (
    <Block className='rise'>
      {(eyebrowParts || isPro) && (
        <Eyebrow>
          {eyebrowParts && <EyebrowGrad>{eyebrowParts}</EyebrowGrad>}
          {isPro && <ProBadge>◆ PRO</ProBadge>}
        </Eyebrow>
      )}
      <NameRow>
        <Box component='h1' sx={{ m: 0, fontSize: 'clamp(30px, 3.4vw, 46px)', fontWeight: 780, letterSpacing: '-0.04em', lineHeight: 1, color: 'var(--ds-night-ink)' }}>
          {firstName} {lastName}
        </Box>
        {userName && <Handle>@{userName}</Handle>}
      </NameRow>
      {motto && <Motto>"{motto}"</Motto>}
      <MetaRow>
        {joinYear && <span>🗓 Member since {joinYear}</span>}
        {galleryCount != null && galleryCount > 0 && (
          <span>◈ {galleryCount} {galleryCount === 1 ? 'gallery' : 'galleries'}</span>
        )}
      </MetaRow>
    </Block>
  )
}
